import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout, Menu, Card, Statistic, Table, Row, Col,
  Typography, Space, Avatar, Progress, Button, Divider, Tag
} from 'antd';
import {
  DollarOutlined, ShoppingOutlined, TeamOutlined,
  ShopOutlined, ToolOutlined, MenuFoldOutlined,
  MenuUnfoldOutlined
} from '@ant-design/icons';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { getUserData } from '../../../hooks/Auth';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

const AdminDashboardd = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const user = getUserData();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch('https://techcrew.pk/ulc/Get_AdminDashboardStats.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ user_id: user?.user?.id || 0 })
        });
        const data = await response.json();
        setStats(data.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching stats:', error);
        setLoading(false);
      }
    };
    
    fetchStats();
  }, []);

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  if (!stats) {
    return <div>Error loading dashboard data</div>;
  }

  // Chart data preparation
  const monthlyTrendData = {
    labels: stats.monthly_trend.map(item => item.month),
    datasets: [
      {
        label: 'Monthly Revenue (SAR)',
        data: stats.monthly_trend.map(item => parseFloat(item.amount)),
        borderColor: '#1890ff',
        backgroundColor: 'rgba(24, 144, 255, 0.1)',
        tension: 0.4,
        fill: true,
        borderWidth: 2
      }
    ]
  };

  // Table columns
  const transactionColumns = [
    {
      title: 'Date & Time',
      dataIndex: 'transaction_date',
      key: 'date',
      width: 180,
      render: date => new Date(date).toLocaleString()
    },
    {
      title: 'User',
      dataIndex: 'user_name',
      key: 'user',
      width: 150
    },
    {
      title: 'Type',
      dataIndex: 'transaction_type',
      key: 'type',
      width: 120,
      render: type => (
        <Tag color={type === 'receive' ? 'green' : 'orange'} style={{ margin: 0 }}>
          {type.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Amount (SAR)',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: amount => (
        <Text strong style={{ color: amount.startsWith('-') ? '#ff4d4f' : '#52c41a' }}>
          {amount}
        </Text>
      )
    },
    {
      title: 'Description',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true
    }
  ];

  const companyColumns = [
    {
      title: 'Company',
      dataIndex: 'company_name',
      key: 'company'
    },
    {
      title: 'Equipment Count',
      dataIndex: 'equipment_count',
      key: 'count',
      render: count => (
        <Progress
          percent={(parseInt(count) / stats.total_equipment) * 100}
          size="small"
          showInfo={false}
        />
      )
    },
    {
      title: 'Count',
      dataIndex: 'equipment_count',
      key: 'count_value',
      render: count => `${count} items`
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sider trigger={null} collapsible collapsed={collapsed} theme="light">
        <div className="logo" style={{ height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {collapsed ? (
            <Title level={4} style={{ margin: 0 }}>ULC</Title>
          ) : (
            <Title level={3} style={{ margin: 0 }}>United Lease Co.</Title>
          )}
        </div>
        <Menu theme="light" mode="inline" defaultSelectedKeys={['1']}>
          <Menu.Item key="1" icon={<DollarOutlined />}>Dashboard</Menu.Item>
          <Menu.Item key="2" icon={<ShoppingOutlined />}>Orders</Menu.Item>
          <Menu.Item key="3" icon={<TeamOutlined />}>Suppliers</Menu.Item>
          <Menu.Item key="4" icon={<ShopOutlined />}>Companies</Menu.Item>
          <Menu.Item key="5" icon={<ToolOutlined />}>Equipment</Menu.Item>
        </Menu>
      </Sider>

      <Layout className="site-layout">
        {/* Header */}
        <Header style={{ padding: 0, background: '#fff', boxShadow: '0 1px 4px rgba(0,21,41,0.08)' }}>
          <Row justify="space-between" align="middle" style={{ padding: '0 24px' }}>
            <Col>
              {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
                className: 'trigger',
                onClick: () => setCollapsed(!collapsed),
                style: { fontSize: '18px' }
              })}
            </Col>
            <Col>
              <Space>
                <Avatar style={{ backgroundColor: '#1890ff' }}>
                  {user.name ? user.name.charAt(0).toUpperCase() : 'A'}
                </Avatar>
                <Text strong>{user.name || 'Admin'}</Text>
              </Space>
            </Col>
          </Row>
        </Header>

        {/* Main Content */}
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff', minHeight: 280 }}>
          {/* Summary Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <Card bordered={false} hoverable>
                <Statistic
                  title="Total Commission"
                  value={stats.total_commission}
                  prefix="SAR"
                  valueStyle={{ color: '#1890ff' }}
                />
                <Progress
                  percent={100}
                  showInfo={false}
                  strokeColor="#1890ff"
                  trailColor="#f0f0f0"
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card bordered={false} hoverable>
                <Statistic
                  title="Total Payments"
                  value={stats.total_payments}
                  prefix="SAR"
                  valueStyle={{ color: '#ff4d4f' }}
                />
                <Progress
                  percent={100}
                  showInfo={false}
                  strokeColor="#ff4d4f"
                  trailColor="#f0f0f0"
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card bordered={false} hoverable>
                <Statistic
                  title="Net Balance"
                  value={stats.net_balance}
                  prefix="SAR"
                  valueStyle={{ color: '#52c41a' }}
                />
                <Progress
                  percent={100}
                  showInfo={false}
                  strokeColor="#52c41a"
                  trailColor="#f0f0f0"
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card bordered={false} hoverable>
                <Statistic
                  title="Total Orders"
                  value={stats.total_orders}
                  suffix="orders"
                />
                <Progress
                  percent={100}
                  showInfo={false}
                  strokeColor="#722ed1"
                  trailColor="#f0f0f0"
                />
              </Card>
            </Col>
          </Row>

          {/* Revenue Chart */}
          

          {/* Tables Row */}
          <Row gutter={[16, 16]}>
            <Col xs={24} xl={16}>
              <Card
                title="Recent Transactions"
                bordered={false}
                extra={<Button type="primary" ghost>View All</Button>}
                hoverable
              >
                <Table
                  columns={transactionColumns}
                  dataSource={stats.recent_transactions}
                  rowKey="transaction_id"
                  pagination={{ pageSize: 5 }}
                  size="middle"
                  scroll={{ x: 800 }}
                  style={{ width: '100%' }}
                />
              </Card>
            </Col>
            <Col xs={24} xl={8}>
              <Card
                title="Top Companies"
                bordered={false}
                extra={<Button type="primary" ghost>View All</Button>}
                hoverable
              >
                <Table
                  columns={companyColumns}
                  dataSource={stats.top_companies}
                  rowKey="company_name"
                  pagination={{ pageSize: 5 }}
                  size="middle"
                />
              </Card>
            </Col>
          </Row>
          <Card 
            title="Monthly Revenue Trend" 
            bordered={false} 
            style={{ marginBottom: 24 }}
            hoverable
          >
            <Line 
              data={monthlyTrendData} 
              options={{ 
                responsive: true,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        return `SAR ${context.raw}`;
                      }
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: function(value) {
                        return `SAR ${value}`;
                      }
                    }
                  }
                }
              }} 
            />
          </Card>
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminDashboardd;